import React, {useState, useRef, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faMoon,
    faSun,
    faSignOutAlt,
    faChevronDown,
    faUserCircle,
    faShieldAlt, faGlobe,
    faTableList,
    faUsersGear,
    faIdCard,
    faMasksTheater,
} from '@fortawesome/free-solid-svg-icons';
import {faGoogle} from '@fortawesome/free-brands-svg-icons';
import {useAuth} from '../../hooks/useAuth';
import {hasLocalHistory} from '../../hooks/useEventHistory';
import {getEventSelection} from '../../utils/eventSelections';
import {useCurrentUserInfo} from "../../hooks/useCurrentUserInfo.ts";
import {isAdmin} from "../../utils/adminConfig";
import { useTranslation } from 'react-i18next';

interface UserDropdownProps {
    darkMode: boolean;
    onToggleDarkMode: () => void;
}

const UserDropdown: React.FC<UserDropdownProps> = ({darkMode, onToggleDarkMode}) => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const {user, logout, signInWithGoogle} = useAuth();
    const {eventId} = useParams<{ eventId?: string }>();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleMenuClick = (action: string) => {
        setIsOpen(false);

        switch (action) {
            case 'my-profile':
                if (user) {
                    if (eventId) {
                        navigate(`/profile/${eventId}/${user.uid}`);
                    } else {
                        navigate('/profile');
                    }
                } else {
                    navigate('/profile');
                }
                break;
            case 'participation-profile':
                if (eventId) {
                    const selectedUserId = getEventSelection(eventId);
                    if (selectedUserId) {
                        navigate(`/profile/${eventId}/${selectedUserId}`);
                    } else {
                        navigate(`/profile/${eventId}`);
                    }
                }
                break;
            case 'events':
                navigate('/my-events');
                break;
            case 'feedback-management':
                navigate('/admin/feedback');
                break;
            case 'event-management':
                navigate('/admin/events');
                break;
            case 'user-management':
                navigate('/admin/users');
                break;
            case 'darkmode':
                onToggleDarkMode();
                break;
            case 'sign-in':
                signInWithGoogle().catch(() => {
                    // Already logged by useAuth — usually just the popup being closed
                });
                break;
            case 'logout':
                handleLogout();
                break;
        }
    };

    const userInfo = useCurrentUserInfo(eventId);
    const selectedUserId = eventId ? getEventSelection(eventId) : null;

    // Signed out, the header has no name to show — and borrowing the picked
    // participant's would claim an identity nobody logged in as.
    const identityName = userInfo.name || t('components.userDropdown.anonymousUser');

    // The profile is reachable whenever there is an identity behind it: an account,
    // or a participant picked in this event.
    const canOpenProfile = userInfo.isLoggedIn || Boolean(eventId && selectedUserId);
    const profileAction = eventId && selectedUserId && selectedUserId !== user?.uid
        ? 'participation-profile'
        : 'my-profile';

    // Signed out, My Events lists what this device has opened — worth a menu
    // item only once there is something in it.
    const canOpenEvents = userInfo.isLoggedIn || hasLocalHistory();

    return (
        <div className="relative" ref={dropdownRef}>
            {/* User Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
                {/* Avatar */}
                <div
                    className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    {userInfo.avatar ? (
                        <img
                            src={userInfo.avatar}
                            alt={identityName}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faUserCircle}
                            className="text-gray-500 dark:text-gray-400 text-lg"
                        />
                    )}
                </div>

                {/* User Name — only once there is one. For a guest it could
                    only say "Anonymous User", which the menu says anyway, and it
                    sat beside the avatar reading like somebody's name. */}
                {userInfo.isLoggedIn && (
                    <div className="hidden sm:block">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {identityName}
                        </span>
                    </div>
                )}

                {/* Dropdown Arrow */}
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-gray-500 dark:text-gray-400 text-xs transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className="glass-dropdown absolute right-0 top-full mt-2 w-56">
                    <div className="py-1">
                        {/* User Info Header — who you are, not a control. It used to be
                            a button that looked like a label, so nobody pressed it; the
                            profile has its own item below now. */}
                        <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {identityName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {userInfo.isLoggedIn
                                    ? t('components.userDropdown.loggedIn')
                                    : t('components.userDropdown.notSignedIn')}
                            </p>
                            {/* The stand-in role, said out loud instead of worn as
                                a name: this is the one place it can be read
                                without being mistaken for who you are. */}
                            {userInfo.actingAsName && (
                                <p className="mt-1 flex items-center text-xs text-blue-600 dark:text-blue-400 truncate">
                                    <FontAwesomeIcon icon={faMasksTheater} className="mr-1.5 flex-shrink-0" />
                                    <span className="min-w-0 truncate">
                                        {t('components.userDropdown.actingAs', { name: userInfo.actingAsName })}
                                    </span>
                                </p>
                            )}
                        </div>

                        {/* Profile */}
                        {canOpenProfile && (
                            <button
                                onClick={() => handleMenuClick(profileAction)}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <FontAwesomeIcon icon={faIdCard}
                                                 className="mr-3 text-gray-500 dark:text-gray-400"/>
                                {eventId
                                    ? t('components.userDropdown.eventProfile')
                                    : t('components.userDropdown.myProfile')}
                            </button>
                        )}

                        {/* My Events */}
                        {canOpenEvents && (
                            <button
                                onClick={() => handleMenuClick('events')}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <FontAwesomeIcon icon={faCalendarAlt}
                                                 className="mr-3 text-gray-500 dark:text-gray-400"/>
                                {t('components.userDropdown.myEvents')}
                            </button>
                        )}

                        {/* Menu Items - Only for logged users */}
                        {userInfo.isLoggedIn && (
                            <React.Fragment>
                                {isAdmin(user?.email) && (
                                    <button
                                        onClick={() => handleMenuClick('feedback-management')}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faShieldAlt}
                                                         className="mr-3 text-gray-500 dark:text-gray-400"/>
                                        {t('components.userDropdown.feedbackManagement')}
                                    </button>
                                )}

                                {/* Admin - Event Management */}
                                {isAdmin(user?.email) && (
                                    <button
                                        onClick={() => handleMenuClick('event-management')}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faTableList}
                                                         className="mr-3 text-gray-500 dark:text-gray-400"/>
                                        {t('components.userDropdown.eventManagement')}
                                    </button>
                                )}

                                {/* Admin - User Management */}
                                {isAdmin(user?.email) && (
                                    <button
                                        onClick={() => handleMenuClick('user-management')}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faUsersGear}
                                                         className="mr-3 text-gray-500 dark:text-gray-400"/>
                                        {t('components.userDropdown.userManagement')}
                                    </button>
                                )}

                            </React.Fragment>
                        )}

                        {/* Divider — only with items above it: straight under the
                            header's own rule it drew a double line */}
                        {(canOpenProfile || canOpenEvents) && (
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        )}

                        {/* Theme Toggle */}
                        <button
                            onClick={() => handleMenuClick('darkmode')}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <FontAwesomeIcon
                                icon={darkMode ? faSun : faMoon}
                                className="mr-3 text-gray-500 dark:text-gray-400"
                            />
                            {darkMode ? t('components.userDropdown.lightMode') : t('components.userDropdown.darkMode')}
                        </button>

                        {/* Language Toggle */}
                        <button
                            onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'vi' : 'en')}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <FontAwesomeIcon
                                icon={faGlobe}
                                className="mr-3 text-gray-500 dark:text-gray-400"
                            />
                            {i18n.language === 'en' ? t('language.vi') : t('language.en')}
                        </button>

                        {/* Sign in - the guest's counterpart to Logout, and the only
                            one in the home page header, which keeps to this menu */}
                        {!userInfo.isLoggedIn && (
                            <>
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                <button
                                    onClick={() => handleMenuClick('sign-in')}
                                    className="flex items-center w-full px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faGoogle} className="mr-3"/>
                                    {t('auth.signIn')}
                                </button>
                            </>
                        )}

                        {/* Logout - Only for logged users */}
                        {userInfo.isLoggedIn && (
                            <>
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                <button
                                    onClick={() => handleMenuClick('logout')}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faSignOutAlt}
                                                     className="mr-3 text-red-500 dark:text-red-400"/>
                                    {t('components.userDropdown.logout')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDropdown;